import { useState, useRef, useEffect } from "react";
import Webcam from "react-webcam";
import Tesseract from "tesseract.js";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
// Tabs removed - camera only mode
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Loader2,
  Check,
  RefreshCw,
  Move,
  Save,
  Trash2,
  Settings,
  Plus,
  Maximize2,
} from "lucide-react";
import { toast } from "react-toastify";
import { supabase } from "@/lib/supabase";

export default function AdvancedCardOCR({
  isOpen,
  onClose,
  onCardDataExtracted,
  initialImage = null,
  startWithCamera = false,
}) {
  const [step, setStep] = useState(startWithCamera ? 1 : initialImage ? 2 : 1); // 1: Capture, 2: Detect Card, 3: Define Regions, 4: OCR
  const [processing, setProcessing] = useState(false);
  const [capturedImage, setCapturedImage] = useState(initialImage);
  const [rectifiedImage, setRectifiedImage] = useState(null);
  const [cardCorners, setCardCorners] = useState([]);
  const [draggingCorner, setDraggingCorner] = useState(null);
  const [cardDetected, setCardDetected] = useState(false);
  const [detectionInterval, setDetectionInterval] = useState(null);
  const [realtimeCorners, setRealtimeCorners] = useState([]); // For live preview
  const [detectionConfidence, setDetectionConfidence] = useState(0); // Detection confidence %
  const [ocrResults, setOcrResults] = useState({}); // OCR results for each region
  const [debugInfo, setDebugInfo] = useState({
    opencvReady: false,
    detecting: false,
    lastError: null,
    contourCount: 0,
    largestContourArea: 0,
    processingTime: 0,
    fourCornerContours: 0,
    bestRatio: 0,
    bestConfidence: 0,
    inGuide: false,
  });
  const [layouts, setLayouts] = useState([]);
  const [selectedLayout, setSelectedLayout] = useState(null);
  // ISO 7810 ID-1 standard ROI definitions (in percentages)
  const ISO_7810_ROI = [
    {
      field: "proxyNumber",
      x: 10,
      y: 25,
      width: 80,
      height: 12,
      whitelist: "0123456789",
      label: "Proxy Number",
    },
    {
      field: "cardNumber",
      x: 10,
      y: 45,
      width: 80,
      height: 15,
      whitelist: "0123456789",
      label: "Card Number",
    },
    {
      field: "expirationDate",
      x: 10,
      y: 65,
      width: 30,
      height: 10,
      whitelist: "0123456789/",
      label: "MM/YY",
    },
    {
      field: "cvc",
      x: 70,
      y: 65,
      width: 20,
      height: 10,
      whitelist: "0123456789",
      label: "CVC",
    },
  ];

  const [currentLayout, setCurrentLayout] = useState({
    name: "default",
    regions: ISO_7810_ROI,
  });
  const [extractedData, setExtractedData] = useState({
    cardNumber: "",
    expirationDate: "",
    cvc: "",
    proxyNumber: "",
  });
  const [activeRegion, setActiveRegion] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const webcamRef = useRef(null);

  // Load layouts from settings
  useEffect(() => {
    loadLayouts();
  }, []);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      // Reset to initial state
      setStep(1);
      setCapturedImage(null);
      setRectifiedImage(null);
      setCardCorners([]);
      setExtractedData({
        cardNumber: "",
        expirationDate: "",
        cvc: "",
        proxyNumber: "",
      });
      setCardDetected(false);
      setDetectionConfidence(0);
      setRealtimeCorners([]);
      setActiveRegion(null);
      setIsDragging(false);
      setIsResizing(false);
      setOcrResults({});
      setProcessing(false);

      // Reset layout to default if available
      if (layouts.length > 0) {
        setCurrentLayout(layouts[0]);
      } else {
        setCurrentLayout({
          name: "default",
          regions: ISO_7810_ROI,
        });
      }

      // Restart detection if camera is ready
      if (step === 1 && startWithCamera && webcamRef.current) {
        const interval = setInterval(async () => {
          await detectCardInWebcam();
        }, 1000);
        setDetectionInterval(interval);
      }
    } else {
      // Clean up when closing
      if (detectionInterval) {
        clearInterval(detectionInterval);
        setDetectionInterval(null);
      }
    }
  }, [isOpen]);

  // Reset to default layout
  const resetToDefaultLayout = async () => {
    const defaultLayout = {
      name: "default",
      regions: [
        {
          field: "proxyNumber",
          x: 10,
          y: 25,
          width: 80,
          height: 12,
          whitelist: "0123456789",
        },
        {
          field: "cardNumber",
          x: 10,
          y: 45,
          width: 80,
          height: 15,
          whitelist: "0123456789",
        },
        {
          field: "expirationDate",
          x: 10,
          y: 65,
          width: 30,
          height: 10,
          whitelist: "0123456789/",
        },
        {
          field: "cvc",
          x: 70,
          y: 65,
          width: 20,
          height: 10,
          whitelist: "0123456789",
        },
      ],
    };

    try {
      const { error } = await supabase
        .from("site_settings")
        .upsert({
          key: "card_ocr_layouts",
          value: JSON.stringify([defaultLayout]),
          category: "ocr",
          description: "Card OCR layout configurations",
          updated_at: new Date().toISOString(),
        })
        .select();

      if (error) {
        console.error("Supabase error:", error);
        throw error;
      }

      setLayouts([defaultLayout]);
      setSelectedLayout(defaultLayout);
      setCurrentLayout(defaultLayout);
      toast.success("Reset to default layout");
    } catch (error) {
      console.error("Error resetting layout:", error);
      toast.error(`Failed to reset layout: ${error.message || error}`);
    }
  };

  // Step 2 removed - no corner detection needed

  // Real-time card detection in Step 1
  useEffect(() => {
    let interval = null;

    // Only start detection if modal is open and we're on step 1
    if (isOpen && step === 1 && !cardDetected) {
      console.log("Starting card detection for step 1");

      // Start detection interval
      interval = setInterval(async () => {
        await detectCardInWebcam();
      }, 1000); // Check every 1 second

      setDetectionInterval(interval);
    }

    // Clean up when leaving step 1, closing modal, or card is detected
    return () => {
      if (interval) {
        clearInterval(interval);
        setDetectionInterval(null);
      }
    };
  }, [step, isOpen, cardDetected]);

  const loadLayouts = async () => {
    try {
      const { data, error } = await supabase
        .from("site_settings")
        .select("value")
        .eq("key", "card_ocr_layouts")
        .maybeSingle();

      if (data && data.value) {
        const parsedLayouts = JSON.parse(data.value);
        // Ensure regions is always an array
        const validLayouts = parsedLayouts.map((layout) => ({
          ...layout,
          regions: Array.isArray(layout.regions) ? layout.regions : [],
        }));
        setLayouts(validLayouts);
        if (validLayouts.length > 0) {
          setSelectedLayout(validLayouts[0]);
          setCurrentLayout(validLayouts[0]);
        }
      } else {
        // Use default layout if none exists
        const defaultLayout = {
          name: "default",
          regions: [
            {
              field: "cardNumber",
              x: 10,
              y: 45,
              width: 80,
              height: 15,
              whitelist: "0123456789",
            },
            {
              field: "expirationDate",
              x: 10,
              y: 65,
              width: 30,
              height: 10,
              whitelist: "0123456789/",
            },
            {
              field: "cvc",
              x: 70,
              y: 65,
              width: 20,
              height: 10,
              whitelist: "0123456789",
            },
            {
              field: "proxyNumber",
              x: 10,
              y: 25,
              width: 80,
              height: 12,
              whitelist: "0123456789",
            },
          ],
        };

        setLayouts([defaultLayout]);
        setSelectedLayout(defaultLayout);
        setCurrentLayout(defaultLayout);

        // Save default layout to database
        const { error: saveError } = await supabase
          .from("site_settings")
          .upsert({
            key: "card_ocr_layouts",
            value: JSON.stringify([defaultLayout]),
            category: "ocr",
            description: "Card OCR layout configurations",
            updated_at: new Date().toISOString(),
          })
          .select();

        if (saveError) {
          console.error("Error saving default layout:", saveError);
        }

        console.log("Created default layout");
      }
    } catch (error) {
      console.error("Error loading layouts:", error);

      // Fallback to hardcoded default if database fails
      const fallbackLayout = {
        name: "default",
        regions: [
          {
            field: "proxyNumber",
            x: 10,
            y: 25,
            width: 80,
            height: 12,
            whitelist: "0123456789",
          },
          {
            field: "cardNumber",
            x: 10,
            y: 45,
            width: 80,
            height: 15,
            whitelist: "0123456789",
          },
          {
            field: "expirationDate",
            x: 10,
            y: 65,
            width: 30,
            height: 10,
            whitelist: "0123456789/",
          },
          {
            field: "cvc",
            x: 70,
            y: 65,
            width: 20,
            height: 10,
            whitelist: "0123456789",
          },
        ],
      };
      setCurrentLayout(fallbackLayout);
    }
  };

  const saveLayout = async () => {
    if (!currentLayout.name) {
      toast.error("Please enter a layout name");
      return;
    }

    try {
      // Add or update layout
      const newLayouts = [...layouts];
      const existingIndex = newLayouts.findIndex(
        (l) => l.name === currentLayout.name
      );

      if (existingIndex >= 0) {
        newLayouts[existingIndex] = currentLayout;
      } else {
        newLayouts.push(currentLayout);
      }

      // First, check if record exists
      const { data: existing } = await supabase
        .from("site_settings")
        .select("id")
        .eq("key", "card_ocr_layouts")
        .maybeSingle();

      let error;
      if (existing) {
        // Update existing record
        const { error: updateError } = await supabase
          .from("site_settings")
          .update({
            value: JSON.stringify(newLayouts),
            updated_at: new Date().toISOString(),
          })
          .eq("key", "card_ocr_layouts");
        error = updateError;
      } else {
        // Insert new record
        const { error: insertError } = await supabase
          .from("site_settings")
          .insert({
            key: "card_ocr_layouts",
            value: JSON.stringify(newLayouts),
            category: "ocr",
            description: "Card OCR layout configurations",
            updated_at: new Date().toISOString(),
          });
        error = insertError;
      }

      if (error) {
        console.error("Supabase error:", error);
        throw error;
      }

      console.log("Layout saved successfully");
      setLayouts(newLayouts);
      toast.success("Layout saved successfully!");
    } catch (error) {
      console.error("Error saving layout:", error);
      toast.error(`Failed to save layout: ${error.message || error}`);
    }
  };

  const deleteLayout = async (layoutName) => {
    // Prevent deleting default layout
    if (!layoutName || layoutName === "default") {
      toast.error("Cannot delete the default template");
      return;
    }

    // Confirm deletion
    if (
      !confirm(
        `Delete template "${layoutName}"?\n\nThis action cannot be undone.`
      )
    ) {
      return;
    }

    try {
      const newLayouts = layouts.filter((l) => l.name !== layoutName);

      // Always update, since the record should exist
      const { error } = await supabase
        .from("site_settings")
        .update({
          value: JSON.stringify(newLayouts),
          updated_at: new Date().toISOString(),
        })
        .eq("key", "card_ocr_layouts");

      if (error) {
        console.error("Supabase delete error:", error);
        throw error;
      }
      setLayouts(newLayouts);
      if (selectedLayout?.name === layoutName) {
        setSelectedLayout(newLayouts[0] || null);
        setCurrentLayout(newLayouts[0] || currentLayout);
      }
      toast.success("Layout deleted");
    } catch (error) {
      console.error("Error deleting layout:", error);
      toast.error(`Failed to delete layout: ${error.message || error}`);
    }
  };

  // OCR detection with strict validation for each field format
  const detectCardWithLayout = async () => {
    if (
      !webcamRef.current ||
      !currentLayout ||
      !Array.isArray(currentLayout.regions)
    )
      return false;

    try {
      const imageSrc = webcamRef.current.getScreenshot();
      if (!imageSrc) return false;

      // Try OCR on card number region first (most reliable)
      const cardNumberRegion = currentLayout.regions.find(
        (r) => r.field === "cardNumber"
      );
      if (cardNumberRegion) {
        const result = await performQuickOCR(imageSrc, cardNumberRegion);
        // Card number: exactly 16 digits (4 groups of 4)
        const digitsOnly = result ? result.replace(/\D/g, "") : "";

        // Strict validation: must be exactly 16 digits
        if (digitsOnly.length === 16) {
          console.log("✅ Perfect card number detected:", digitsOnly);
          return true;
        } else if (digitsOnly.length >= 15 && digitsOnly.length <= 16) {
          // Allow 15-16 for slight OCR errors
          console.log(
            "⚠️ Near-match card number:",
            digitsOnly.length,
            "digits"
          );
          return true;
        }
      }

      return false;
    } catch (error) {
      console.error("Layout detection error:", error);
      return false;
    }
  };

  // Quick OCR check on a region
  const performQuickOCR = (imageSrc, region) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = async () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        // Calculate region in pixels
        const x = (region.x / 100) * img.width;
        const y = (region.y / 100) * img.height;
        const width = (region.width / 100) * img.width;
        const height = (region.height / 100) * img.height;

        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, x, y, width, height, 0, 0, width, height);

        try {
          const result = await Tesseract.recognize(canvas.toDataURL(), "eng", {
            tessedit_char_whitelist: "0123456789",
          });

          const cleanText = result.data.text.replace(/\D/g, "");
          resolve(cleanText);
        } catch (error) {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = imageSrc;
    });
  };

  // Detect card in real-time from webcam
  const detectCardInWebcam = async () => {
    if (
      !webcamRef.current ||
      !currentLayout ||
      !Array.isArray(currentLayout.regions)
    ) {
      setDebugInfo((prev) => ({
        ...prev,
        lastError: "Layout not ready",
        detecting: false,
      }));
      return;
    }

    const startTime = Date.now();
    setDebugInfo((prev) => ({ ...prev, detecting: true, lastError: null }));

    try {
      // Use simple layout-based OCR detection
      const detected = await detectCardWithLayout();
      const processingTime = Date.now() - startTime;

      setDebugInfo((prev) => ({
        ...prev,
        detecting: false,
        processingTime,
        lastError: null,
        contourCount: currentLayout?.regions?.length || 0,
      }));

      if (detected) {
        // Card detected via OCR
        setDetectionConfidence(95);
        setCardDetected(true); // This will trigger useEffect cleanup

        // Capture and skip directly to OCR step (skip Step 2)
        setTimeout(() => {
          if (webcamRef.current) {
            const capturedImg = webcamRef.current.getScreenshot();
            setCapturedImage(capturedImg);
            setRectifiedImage(capturedImg); // Use original image
            setStep(3); // Skip Step 2, go directly to OCR step
            toast.success("Card detected! Processing OCR...");
          }
        }, 500);
      } else {
        setCardDetected(false);
        setDetectionConfidence(0);
      }
    } catch (error) {
      console.error("Detection error:", error);
      setDebugInfo((prev) => ({
        ...prev,
        lastError: error.message,
        detecting: false,
      }));
    }
  };

  // Step 1: Capture image and go to OCR
  const capturePhoto = () => {
    const imageSrc = webcamRef.current.getScreenshot();
    setCapturedImage(imageSrc);
    setRectifiedImage(imageSrc); // Use original image
    setStep(3); // Skip corner detection, go to OCR
    toast.success("Image captured! Processing OCR...");
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setCapturedImage(e.target.result);
        setRectifiedImage(e.target.result); // Use original image
        setStep(3); // Skip corner detection
      };
      reader.readAsDataURL(file);
    }
  };

  // Auto-detect card corners using OpenCV.js
  const autoDetectCardCorners = () => {
    if (!capturedImage) return;

    // Check if OpenCV is ready
    if (!window.cv || !window.cvReady) {
      console.log("OpenCV.js not ready, falling back to simple detection");
      autoDetectCardCornersSimple();
      return;
    }

    setProcessing(true);
    const img = new Image();
    img.onload = () => {
      try {
        const corners = detectCardWithOpenCV(img);

        if (corners && corners.length === 4) {
          // Convert to percentage coordinates
          const percentCorners = corners.map((corner) => ({
            x: (corner.x / img.width) * 100,
            y: (corner.y / img.height) * 100,
          }));

          setCardCorners(percentCorners);
          toast.success("Card detected! Adjust corners if needed.");

          // Auto-process after a short delay
          setTimeout(() => {
            rectifyCard(percentCorners);
          }, 1500);
        } else {
          toast.info("Please click the 4 corners of the card manually.");
        }
      } catch (error) {
        console.error("OpenCV detection failed:", error);
        autoDetectCardCornersSimple();
      }

      setProcessing(false);
    };

    img.src = capturedImage;
  };

  // OpenCV-based card detection with confidence score
  const detectCardWithOpenCVAndConfidence = (img) => {
    const cv = window.cv;

    // Define guide area with ISO 7810 ratio (1.586:1)
    // Center the card guide with proper aspect ratio
    const cardGuideHeight = img.height * 0.6; // 60% of screen height
    const cardGuideWidth = cardGuideHeight * 1.586; // ISO 7810 ratio

    // If width exceeds screen, scale down
    let finalWidth = cardGuideWidth;
    let finalHeight = cardGuideHeight;
    if (cardGuideWidth > img.width * 0.85) {
      finalWidth = img.width * 0.85;
      finalHeight = finalWidth / 1.586;
    }

    const guideArea = {
      x: (img.width - finalWidth) / 2,
      y: (img.height - finalHeight) / 2,
      width: finalWidth,
      height: finalHeight,
    };

    // Create canvas and draw image
    const canvas = document.createElement("canvas");
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0);

    // Convert to OpenCV Mat
    let src = cv.imread(canvas);
    let gray = new cv.Mat();
    let edges = new cv.Mat();
    let dilated = new cv.Mat();
    let contours = new cv.MatVector();
    let hierarchy = new cv.Mat();

    try {
      // Convert to grayscale
      cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY);

      // Apply Gaussian blur for noise reduction
      let blurred = new cv.Mat();
      cv.GaussianBlur(gray, blurred, new cv.Size(5, 5), 0);

      // Apply Canny edge detection with lower thresholds for better detection
      cv.Canny(blurred, edges, 30, 100);

      // Dilate edges to connect broken lines more aggressively
      let kernel = cv.getStructuringElement(cv.MORPH_RECT, new cv.Size(5, 5));
      cv.dilate(edges, dilated, kernel, new cv.Point(-1, -1), 2);

      // Clean up
      blurred.delete();

      // Find contours
      cv.findContours(
        dilated,
        contours,
        hierarchy,
        cv.RETR_EXTERNAL,
        cv.CHAIN_APPROX_SIMPLE
      );

      // Update debug info with contour count
      const contourCount = contours.size();
      let fourCornerCount = 0;
      let bestRatioFound = 0;

      let bestMatch = null;
      let maxConfidence = 0;
      let largestArea = 0;
      let largestRectArea = 0;

      console.log(`Found ${contourCount} contours`);

      // First pass: find the largest rectangular contour
      let allCandidates = [];
      let simpleRectangle = null;
      let maxRectScore = 0;

      for (let i = 0; i < contours.size(); i++) {
        let contour = contours.get(i);
        let area = cv.contourArea(contour);

        // Get bounding rectangle to check if contour is within guide area
        let rect = cv.boundingRect(contour);
        const centerX = rect.x + rect.width / 2;
        const centerY = rect.y + rect.height / 2;

        // Check if contour center is within guide area
        const inGuideArea =
          centerX >= guideArea.x &&
          centerX <= guideArea.x + guideArea.width &&
          centerY >= guideArea.y &&
          centerY <= guideArea.y + guideArea.height;

        // Track largest areas for debugging
        if (area > largestArea) {
          largestArea = area;
        }
        if (boundingArea > largestRectArea) {
          largestRectArea = boundingArea;
          setDebugInfo((prev) => ({
            ...prev,
            largestContourArea: Math.round(
              (boundingArea / (img.width * img.height)) * 100
            ),
            inGuide: inGuideArea ? "✓" : "✗",
          }));
        }

        // Much lower threshold since edge contours have small areas
        // Focus on bounding rect size instead
        const boundingArea = rect.width * rect.height;
        const imageArea = img.width * img.height;

        // Simple rectangle detection for guide area
        if (inGuideArea) {
          const rectScore = boundingArea / imageArea;
          const rectRatio = rect.width / rect.height;
          const isCardLike = rectRatio > 1.4 && rectRatio < 1.8;

          if (isCardLike && rectScore > maxRectScore) {
            maxRectScore = rectScore;
            // Create a simple rectangle from bounding box
            simpleRectangle = [
              { x: rect.x, y: rect.y },
              { x: rect.x + rect.width, y: rect.y },
              { x: rect.x + rect.width, y: rect.y + rect.height },
              { x: rect.x, y: rect.y + rect.height },
            ];
          }
        }

        // Accept if: in guide OR has decent bounding area OR minimum contour area
        const minBoundingArea = imageArea * 0.05; // 5% of image
        const minContourArea = imageArea * 0.00005; // Very low threshold

        if (
          inGuideArea ||
          boundingArea > minBoundingArea ||
          area > minContourArea
        ) {
          let peri = cv.arcLength(contour, true);

          // Try different epsilon values to get 4 corners
          const epsilonValues = [0.01, 0.015, 0.02, 0.025, 0.03, 0.04, 0.05];
          let foundCard = false;

          for (let epsilon of epsilonValues) {
            let approx = new cv.Mat();
            cv.approxPolyDP(contour, approx, epsilon * peri, true);

            // Count 4-corner contours
            if (approx.rows === 4) {
              fourCornerCount++;
              console.log(
                `Contour ${i}: 4 corners found! area=${Math.round(
                  (area / (img.width * img.height)) * 100
                )}%, inGuide=${inGuideArea}, epsilon=${epsilon}`
              );
            }

            if (approx.rows === 4 && !foundCard) {
              const corners = [];
              for (let j = 0; j < 4; j++) {
                corners.push({
                  x: approx.data32S[j * 2],
                  y: approx.data32S[j * 2 + 1],
                });
              }

              // Calculate confidence based on multiple factors
              let confidence = 0;

              // 1. Area ratio (30% weight)
              const areaRatio = area / (img.width * img.height);
              const areaScore = Math.min(areaRatio / 0.1, 1) * 30; // Changed from 0.5 to 0.1 for smaller cards
              confidence += areaScore;

              // 2. Guide area bonus (20% weight)
              if (inGuideArea) {
                confidence += 20; // Bonus for being in guide area
              }

              // 3. ISO 7810 Aspect ratio check (35% weight - increased importance)
              const sortedCorners = sortCornersOpenCV(corners);
              const width = Math.sqrt(
                Math.pow(sortedCorners[1].x - sortedCorners[0].x, 2) +
                  Math.pow(sortedCorners[1].y - sortedCorners[0].y, 2)
              );
              const height = Math.sqrt(
                Math.pow(sortedCorners[3].x - sortedCorners[0].x, 2) +
                  Math.pow(sortedCorners[3].y - sortedCorners[0].y, 2)
              );
              const aspectRatio = width / height;

              // ISO 7810 ID-1 format: 85.6mm × 53.98mm = 1.586:1
              const ISO_7810_RATIO = 1.586;
              const RATIO_TOLERANCE = 0.15; // Allow 15% deviation for perspective

              const ratioDiff = Math.abs(aspectRatio - ISO_7810_RATIO);
              const ratioScore =
                ratioDiff <= RATIO_TOLERANCE
                  ? (1 - ratioDiff / RATIO_TOLERANCE) * 35
                  : 0;
              confidence += ratioScore;

              // Track best ratio for debugging
              if (
                Math.abs(aspectRatio - ISO_7810_RATIO) <
                Math.abs(bestRatioFound - ISO_7810_RATIO)
              ) {
                bestRatioFound = aspectRatio;
              }

              console.log(
                `  Aspect ratio: ${aspectRatio.toFixed(
                  3
                )} (ideal: ${ISO_7810_RATIO}, diff: ${ratioDiff.toFixed(3)})`
              );

              // Be more lenient with ratio check - perspective can distort
              if (ratioDiff > RATIO_TOLERANCE * 3) {
                // Increased tolerance
                console.log(`  Skipping: ratio too far off`);
                approx.delete();
                continue; // Skip this contour
              }

              // 4. Rectangularity (15% weight - reduced)
              // Check if angles are close to 90 degrees
              let angleScore = 15;
              for (let k = 0; k < 4; k++) {
                const p1 = sortedCorners[k];
                const p2 = sortedCorners[(k + 1) % 4];
                const p3 = sortedCorners[(k + 2) % 4];

                const v1 = { x: p1.x - p2.x, y: p1.y - p2.y };
                const v2 = { x: p3.x - p2.x, y: p3.y - p2.y };

                const dot = v1.x * v2.x + v1.y * v2.y;
                const mag1 = Math.sqrt(v1.x * v1.x + v1.y * v1.y);
                const mag2 = Math.sqrt(v2.x * v2.x + v2.y * v2.y);

                if (mag1 > 0 && mag2 > 0) {
                  const cosAngle = dot / (mag1 * mag2);
                  const angle =
                    (Math.acos(Math.min(1, Math.max(-1, cosAngle))) * 180) /
                    Math.PI;
                  const angleDiff = Math.abs(90 - angle);
                  angleScore -= angleDiff * 0.2;
                }
              }
              confidence += Math.max(0, angleScore);

              console.log(
                `  Total confidence: ${confidence.toFixed(
                  1
                )}% (area: ${areaScore.toFixed(1)}, guide: ${
                  inGuideArea ? 20 : 0
                }, ratio: ${ratioScore.toFixed(
                  1
                )}, angles: ${angleScore.toFixed(1)})`
              );

              // Store all candidates for comparison
              allCandidates.push({
                corners: sortedCorners,
                confidence: confidence,
                area: area,
                boundingArea: rect.width * rect.height,
                inGuide: inGuideArea,
                aspectRatio: aspectRatio,
              });

              if (confidence > maxConfidence) {
                maxConfidence = confidence;
                bestMatch = sortedCorners;
                foundCard = true; // Mark as found to skip other epsilon values
                console.log(`  ✓ New best match!`);
              }
            }

            approx.delete();
          }
        }
        contour.delete();
      }

      // Clean up
      kernel.delete();
      src.delete();
      gray.delete();
      edges.delete();
      dilated.delete();
      contours.delete();
      hierarchy.delete();

      // If we found a simple rectangle in the guide area but no proper card, use it
      if (
        !bestMatch &&
        !allCandidates.length &&
        simpleRectangle &&
        maxRectScore > 0.1
      ) {
        console.log(
          `Using simple rectangle detection: area=${Math.round(
            maxRectScore * 100
          )}%`
        );
        return {
          corners: simpleRectangle,
          confidence: 55, // Medium confidence for simple detection
        };
      }

      // If no good match found but we have candidates, pick the best one
      if (!bestMatch && allCandidates.length > 0) {
        // Sort by: 1) in guide area, 2) aspect ratio closeness to 1.586, 3) bounding area
        allCandidates.sort((a, b) => {
          // Prioritize guide area
          if (a.inGuide !== b.inGuide) return b.inGuide ? 1 : -1;

          // Then aspect ratio closeness to ISO standard
          const aDiff = Math.abs(a.aspectRatio - 1.586);
          const bDiff = Math.abs(b.aspectRatio - 1.586);
          if (Math.abs(aDiff - bDiff) > 0.1) return aDiff - bDiff;

          // Finally, larger bounding area
          return b.boundingArea - a.boundingArea;
        });

        const best = allCandidates[0];
        console.log(
          `Falling back to best candidate: ratio=${best.aspectRatio.toFixed(
            2
          )}, inGuide=${best.inGuide}, area=${Math.round(
            (best.boundingArea / (img.width * img.height)) * 100
          )}%`
        );

        return {
          corners: best.corners,
          confidence: Math.max(50, Math.round(best.confidence)), // At least 50% for any detected card
        };
      }

      if (bestMatch) {
        return {
          corners: bestMatch,
          confidence: Math.round(maxConfidence),
        };
      }

      return null;
    } catch (error) {
      console.error("OpenCV detection error:", error);
      // Clean up on error
      if (src) src.delete();
      if (gray) gray.delete();
      if (edges) edges.delete();
      if (dilated) dilated.delete();
      if (contours) contours.delete();
      if (hierarchy) hierarchy.delete();
      return null;
    }
  };

  // OpenCV-based card detection with enhanced processing
  const detectCardWithOpenCV = (img) => {
    const cv = window.cv;

    // Create canvas and draw image
    const canvas = document.createElement("canvas");
    canvas.width = img.width;
    canvas.height = img.height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0);

    // Convert to OpenCV Mat
    let src = cv.imread(canvas);
    let gray = new cv.Mat();
    let edges = new cv.Mat();
    let dilated = new cv.Mat();
    let contours = new cv.MatVector();
    let hierarchy = new cv.Mat();

    try {
      // Convert to grayscale
      cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY);

      // Apply bilateral filter to reduce noise while keeping edges sharp
      let bilateral = new cv.Mat();
      cv.bilateralFilter(gray, bilateral, 9, 75, 75, cv.BORDER_DEFAULT);

      // Apply adaptive threshold for better edge detection in varying lighting
      let thresh = new cv.Mat();
      cv.adaptiveThreshold(
        bilateral,
        thresh,
        255,
        cv.ADAPTIVE_THRESH_GAUSSIAN_C,
        cv.THRESH_BINARY,
        11,
        2
      );

      // Apply morphological operations to connect nearby edges
      let kernel = cv.getStructuringElement(cv.MORPH_RECT, new cv.Size(3, 3));
      cv.morphologyEx(thresh, thresh, cv.MORPH_CLOSE, kernel);

      // Apply Canny edge detection with multiple threshold levels
      let cannyResults = [];
      const thresholdPairs = [
        [30, 100],
        [50, 150],
        [75, 200],
        [100, 250],
      ];

      for (let [low, high] of thresholdPairs) {
        let tempEdges = new cv.Mat();
        cv.Canny(bilateral, tempEdges, low, high);

        // Dilate edges to connect broken lines
        cv.dilate(tempEdges, tempEdges, kernel, new cv.Point(-1, -1), 1);

        // Find contours for this threshold pair
        let tempContours = new cv.MatVector();
        let tempHierarchy = new cv.Mat();
        cv.findContours(
          tempEdges,
          tempContours,
          tempHierarchy,
          cv.RETR_EXTERNAL,
          cv.CHAIN_APPROX_SIMPLE
        );

        // Look for rectangular contours
        for (let i = 0; i < tempContours.size(); i++) {
          let contour = tempContours.get(i);
          let area = cv.contourArea(contour);

          // Check if contour is large enough (at least 20% of image)
          if (area > img.width * img.height * 0.2) {
            let peri = cv.arcLength(contour, true);
            let approx = new cv.Mat();

            // Try different epsilon values for polygon approximation
            for (let epsilonFactor of [0.01, 0.02, 0.03, 0.04, 0.05]) {
              cv.approxPolyDP(contour, approx, epsilonFactor * peri, true);

              // Check if we got exactly 4 corners
              if (approx.rows === 4) {
                // Verify it's roughly rectangular
                const corners = [];
                for (let j = 0; j < 4; j++) {
                  corners.push({
                    x: approx.data32S[j * 2],
                    y: approx.data32S[j * 2 + 1],
                  });
                }

                // Check aspect ratio (cards are typically 1.586:1)
                const sortedCorners = sortCornersOpenCV(corners);
                const width = Math.sqrt(
                  Math.pow(sortedCorners[1].x - sortedCorners[0].x, 2) +
                    Math.pow(sortedCorners[1].y - sortedCorners[0].y, 2)
                );
                const height = Math.sqrt(
                  Math.pow(sortedCorners[3].x - sortedCorners[0].x, 2) +
                    Math.pow(sortedCorners[3].y - sortedCorners[0].y, 2)
                );
                const aspectRatio = width / height;

                // Accept aspect ratios between 1.3 and 1.8 (allowing for perspective)
                if (aspectRatio > 1.3 && aspectRatio < 1.8) {
                  // Clean up before returning
                  approx.delete();
                  contour.delete();
                  tempEdges.delete();
                  tempContours.delete();
                  tempHierarchy.delete();
                  bilateral.delete();
                  thresh.delete();
                  kernel.delete();
                  src.delete();
                  gray.delete();
                  edges.delete();
                  dilated.delete();
                  contours.delete();
                  hierarchy.delete();

                  return sortedCorners;
                }
              }
            }
            approx.delete();
          }
          contour.delete();
        }

        tempEdges.delete();
        tempContours.delete();
        tempHierarchy.delete();
      }

      // If no card found with adaptive methods, try Hough Line detection
      cv.Canny(bilateral, edges, 50, 150);
      cv.dilate(edges, dilated, kernel, new cv.Point(-1, -1), 2);

      let lines = new cv.Mat();
      cv.HoughLinesP(dilated, lines, 1, Math.PI / 180, 100, 50, 10);

      // Try to find 4 lines that form a rectangle
      if (lines.rows > 3) {
        // Process lines to find potential card edges
        // This is a fallback method
        console.log("Trying Hough line detection method...");
      }

      lines.delete();

      // Clean up
      bilateral.delete();
      thresh.delete();
      kernel.delete();
      src.delete();
      gray.delete();
      edges.delete();
      dilated.delete();
      contours.delete();
      hierarchy.delete();

      return null;
    } catch (error) {
      // Clean up on error
      console.error("OpenCV detection error:", error);
      if (src) src.delete();
      if (gray) gray.delete();
      if (edges) edges.delete();
      if (dilated) dilated.delete();
      if (contours) contours.delete();
      if (hierarchy) hierarchy.delete();
      throw error;
    }
  };

  // Sort corners for OpenCV detection
  const sortCornersOpenCV = (corners) => {
    // Calculate center point
    const center = corners.reduce(
      (acc, corner) => ({
        x: acc.x + corner.x / 4,
        y: acc.y + corner.y / 4,
      }),
      { x: 0, y: 0 }
    );

    // Sort corners by angle from center
    const sortedCorners = corners.sort((a, b) => {
      const angleA = Math.atan2(a.y - center.y, a.x - center.x);
      const angleB = Math.atan2(b.y - center.y, b.x - center.x);
      return angleA - angleB;
    });

    // Find top-left (minimum sum of x+y)
    let minSum = Infinity;
    let tlIndex = 0;
    for (let i = 0; i < 4; i++) {
      const sum = sortedCorners[i].x + sortedCorners[i].y;
      if (sum < minSum) {
        minSum = sum;
        tlIndex = i;
      }
    }

    // Reorder starting from top-left
    const result = [];
    for (let i = 0; i < 4; i++) {
      result.push(sortedCorners[(tlIndex + i) % 4]);
    }

    return result;
  };

  // Fallback to simple detection if OpenCV fails
  const autoDetectCardCornersSimple = () => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");

      // Downscale for faster processing
      const scale = Math.min(800 / img.width, 800 / img.height, 1);
      canvas.width = img.width * scale;
      canvas.height = img.height * scale;
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      // Get image data for processing
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const corners = detectCardCornersSimple(
        imageData,
        canvas.width,
        canvas.height
      );

      if (corners && corners.length === 4) {
        // Scale back to original size and convert to percentage
        const percentCorners = corners.map((corner) => ({
          x: (corner.x / scale / img.width) * 100,
          y: (corner.y / scale / img.height) * 100,
        }));

        setCardCorners(percentCorners);
        toast.success("Card detected! Adjust corners if needed.");

        // Auto-process after a short delay
        setTimeout(() => {
          rectifyCard(percentCorners);
        }, 1500);
      } else {
        toast.info("Please click the 4 corners of the card manually.");
      }

      setProcessing(false);
    };

    img.src = capturedImage;
  };

  // Simplified card detection focusing on finding the largest quadrilateral
  const detectCardCornersSimple = (imageData, width, height) => {
    const data = imageData.data;

    // Convert to grayscale and find edges
    const gray = new Uint8ClampedArray(width * height);
    for (let i = 0; i < data.length; i += 4) {
      gray[i / 4] = data[i] * 0.299 + data[i + 1] * 0.587 + data[i + 2] * 0.114;
    }

    // Apply adaptive threshold to create binary image
    const binary = new Uint8ClampedArray(width * height);
    const blockSize = 25;
    const C = 10;

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        let sum = 0;
        let count = 0;

        // Calculate mean in local area
        for (let dy = -blockSize; dy <= blockSize; dy++) {
          for (let dx = -blockSize; dx <= blockSize; dx++) {
            const ny = y + dy;
            const nx = x + dx;
            if (ny >= 0 && ny < height && nx >= 0 && nx < width) {
              sum += gray[ny * width + nx];
              count++;
            }
          }
        }

        const mean = sum / count;
        const idx = y * width + x;
        binary[idx] = gray[idx] > mean - C ? 255 : 0;
      }
    }

    // Find the largest connected component (likely the card)
    const visited = new Uint8Array(width * height);
    let largestComponent = null;
    let largestSize = 0;

    for (let y = 10; y < height - 10; y += 20) {
      for (let x = 10; x < width - 10; x += 20) {
        const idx = y * width + x;
        if (!visited[idx] && binary[idx] === 255) {
          const component = floodFill(binary, visited, x, y, width, height);
          if (component.size > largestSize) {
            largestSize = component.size;
            largestComponent = component;
          }
        }
      }
    }

    if (!largestComponent || largestSize < width * height * 0.1) {
      return null;
    }

    // Find corners of the bounding box
    const bounds = largestComponent.bounds;

    // Return corners with some margin adjustment
    const margin = 0.02; // 2% margin
    const corners = [
      { x: bounds.minX - width * margin, y: bounds.minY - height * margin },
      { x: bounds.maxX + width * margin, y: bounds.minY - height * margin },
      { x: bounds.maxX + width * margin, y: bounds.maxY + height * margin },
      { x: bounds.minX - width * margin, y: bounds.maxY + height * margin },
    ];

    // Clamp to image bounds
    return corners.map((c) => ({
      x: Math.max(0, Math.min(width - 1, c.x)),
      y: Math.max(0, Math.min(height - 1, c.y)),
    }));
  };

  // Flood fill to find connected components
  const floodFill = (binary, visited, startX, startY, width, height) => {
    const stack = [[startX, startY]];
    let minX = startX,
      maxX = startX;
    let minY = startY,
      maxY = startY;
    let size = 0;

    while (stack.length > 0) {
      const [x, y] = stack.pop();
      const idx = y * width + x;

      if (
        x < 0 ||
        x >= width ||
        y < 0 ||
        y >= height ||
        visited[idx] ||
        binary[idx] === 0
      ) {
        continue;
      }

      visited[idx] = 1;
      size++;

      minX = Math.min(minX, x);
      maxX = Math.max(maxX, x);
      minY = Math.min(minY, y);
      maxY = Math.max(maxY, y);

      // Add neighbors
      stack.push([x + 1, y]);
      stack.push([x - 1, y]);
      stack.push([x, y + 1]);
      stack.push([x, y - 1]);
    }

    return {
      size,
      bounds: { minX, maxX, minY, maxY },
    };
  };

  // Step 2: Manual corner selection for card detection
  const handleImageClick = (e) => {
    if (cardCorners.length >= 4 || processing || draggingCorner !== null)
      return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    const newCorners = [...cardCorners, { x, y }];
    setCardCorners(newCorners);

    if (newCorners.length === 4) {
      // All corners selected, show message but don't auto-process
      toast.success(
        "All corners selected! Adjust if needed or click 'Process Card'"
      );
    }
  };

  // Handle corner drag start
  const handleCornerMouseDown = (e, index) => {
    e.stopPropagation();
    setDraggingCorner(index);
  };

  // Handle corner drag
  const handleCornerDrag = (e) => {
    if (draggingCorner === null) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.max(
      0,
      Math.min(100, ((e.clientX - rect.left) / rect.width) * 100)
    );
    const y = Math.max(
      0,
      Math.min(100, ((e.clientY - rect.top) / rect.height) * 100)
    );

    const newCorners = [...cardCorners];
    newCorners[draggingCorner] = { x, y };
    setCardCorners(newCorners);
  };

  // Handle corner drag end
  const handleCornerDragEnd = () => {
    setDraggingCorner(null);
  };

  // Calculate perspective transform matrix
  const getPerspectiveTransform = (src, dst) => {
    // Calculate the perspective transformation matrix
    // This uses a simplified homography calculation
    const matrix = [];
    const A = [];
    const B = [];

    for (let i = 0; i < 4; i++) {
      A.push([
        src[i].x,
        src[i].y,
        1,
        0,
        0,
        0,
        -dst[i].x * src[i].x,
        -dst[i].x * src[i].y,
      ]);
      A.push([
        0,
        0,
        0,
        src[i].x,
        src[i].y,
        1,
        -dst[i].y * src[i].x,
        -dst[i].y * src[i].y,
      ]);
      B.push(dst[i].x);
      B.push(dst[i].y);
    }

    // Solve the system (simplified - in production use proper matrix library)
    // For now, return a basic transform
    return {
      a: 1,
      b: 0,
      c: 0,
      d: 0,
      e: 1,
      f: 0,
      g: 0,
      h: 0,
      i: 1,
    };
  };

  // Apply perspective transform to a point
  const transformPoint = (x, y, matrix) => {
    const w = matrix.g * x + matrix.h * y + matrix.i;
    return {
      x: (matrix.a * x + matrix.b * y + matrix.c) / w,
      y: (matrix.d * x + matrix.e * y + matrix.f) / w,
    };
  };

  // Rectify card image with perspective transform
  const rectifyCard = (corners) => {
    setProcessing(true);

    // Sort corners: top-left, top-right, bottom-right, bottom-left
    const sortedCorners = sortCorners(corners);

    // Small delay to ensure canvas is mounted
    setTimeout(() => {
      const img = new Image();
      img.onload = () => {
        let canvas = rectifiedCanvasRef.current;
        let ctx;

        if (!canvas) {
          // Create canvas dynamically if ref not ready
          canvas = document.createElement("canvas");
        }

        // Use moderate resolution for balance between quality and performance
        const cardWidth = 1284; // 1.5x standard size
        const cardHeight = 810;
        canvas.width = cardWidth;
        canvas.height = cardHeight;
        ctx = canvas.getContext("2d");

        // Disable image smoothing for sharper result
        ctx.imageSmoothingEnabled = false;

        // Convert percentage coordinates to pixel coordinates
        const srcCorners = sortedCorners.map((corner) => ({
          x: (corner.x / 100) * img.width,
          y: (corner.y / 100) * img.height,
        }));

        // Clear canvas
        ctx.fillStyle = "white";
        ctx.fillRect(0, 0, cardWidth, cardHeight);

        // Create temporary canvas for source image
        const srcCanvas = document.createElement("canvas");
        srcCanvas.width = img.width;
        srcCanvas.height = img.height;
        const srcCtx = srcCanvas.getContext("2d");
        srcCtx.drawImage(img, 0, 0);

        // Use higher quality bilinear sampling
        const sampleBilinear = (x, y, imageData, width) => {
          const x1 = Math.floor(x);
          const y1 = Math.floor(y);
          const x2 = Math.min(x1 + 1, width - 1);
          const y2 = Math.min(y1 + 1, imageData.height - 1);

          const fx = x - x1;
          const fy = y - y1;

          const idx1 = (y1 * width + x1) * 4;
          const idx2 = (y1 * width + x2) * 4;
          const idx3 = (y2 * width + x1) * 4;
          const idx4 = (y2 * width + x2) * 4;

          const r =
            imageData.data[idx1] * (1 - fx) * (1 - fy) +
            imageData.data[idx2] * fx * (1 - fy) +
            imageData.data[idx3] * (1 - fx) * fy +
            imageData.data[idx4] * fx * fy;

          const g =
            imageData.data[idx1 + 1] * (1 - fx) * (1 - fy) +
            imageData.data[idx2 + 1] * fx * (1 - fy) +
            imageData.data[idx3 + 1] * (1 - fx) * fy +
            imageData.data[idx4 + 1] * fx * fy;

          const b =
            imageData.data[idx1 + 2] * (1 - fx) * (1 - fy) +
            imageData.data[idx2 + 2] * fx * (1 - fy) +
            imageData.data[idx3 + 2] * (1 - fx) * fy +
            imageData.data[idx4 + 2] * fx * fy;

          return [r, g, b];
        };

        const srcImageData = srcCtx.getImageData(0, 0, img.width, img.height);
        const dstImageData = ctx.createImageData(cardWidth, cardHeight);
        const dstData = dstImageData.data;

        // Perform perspective correction with bilinear sampling
        for (let dy = 0; dy < cardHeight; dy++) {
          for (let dx = 0; dx < cardWidth; dx++) {
            // Calculate normalized coordinates
            const u = dx / (cardWidth - 1);
            const v = dy / (cardHeight - 1);

            // Bilinear interpolation in source space
            const topX = srcCorners[0].x * (1 - u) + srcCorners[1].x * u;
            const topY = srcCorners[0].y * (1 - u) + srcCorners[1].y * u;
            const bottomX = srcCorners[3].x * (1 - u) + srcCorners[2].x * u;
            const bottomY = srcCorners[3].y * (1 - u) + srcCorners[2].y * u;

            const srcX = topX * (1 - v) + bottomX * v;
            const srcY = topY * (1 - v) + bottomY * v;

            if (
              srcX >= 0 &&
              srcX < img.width - 1 &&
              srcY >= 0 &&
              srcY < img.height - 1
            ) {
              const [r, g, b] = sampleBilinear(
                srcX,
                srcY,
                srcImageData,
                img.width
              );
              const dstIdx = (dy * cardWidth + dx) * 4;

              // Apply slight contrast boost
              const contrast = 1.2;
              const brightness = 10;

              dstData[dstIdx] = Math.min(
                255,
                Math.max(0, (r - 128) * contrast + 128 + brightness)
              );
              dstData[dstIdx + 1] = Math.min(
                255,
                Math.max(0, (g - 128) * contrast + 128 + brightness)
              );
              dstData[dstIdx + 2] = Math.min(
                255,
                Math.max(0, (b - 128) * contrast + 128 + brightness)
              );
              dstData[dstIdx + 3] = 255;
            } else {
              // White background for out of bounds
              const dstIdx = (dy * cardWidth + dx) * 4;
              dstData[dstIdx] = 255;
              dstData[dstIdx + 1] = 255;
              dstData[dstIdx + 2] = 255;
              dstData[dstIdx + 3] = 255;
            }
          }
        }

        ctx.putImageData(dstImageData, 0, 0);

        // Export as PNG
        const rectifiedDataUrl = canvas.toDataURL("image/png");
        setRectifiedImage(rectifiedDataUrl);
        toast.success(
          "Card perspective corrected! Now adjust the OCR regions."
        );
        setStep(3);
        setProcessing(false);
      };

      img.onerror = () => {
        toast.error("Failed to load image. Please try again.");
        setProcessing(false);
      };

      img.src = capturedImage;
    }, 100);
  };

  // Sort corners to correct order (TL, TR, BR, BL)
  const sortCorners = (corners) => {
    // Make a copy to avoid mutating original
    const points = [...corners];

    // Sort by Y coordinate first to separate top from bottom
    points.sort((a, b) => a.y - b.y);

    // Top two points (smallest Y values)
    const topPoints = points.slice(0, 2);
    // Bottom two points (largest Y values)
    const bottomPoints = points.slice(2, 4);

    // Sort top points by X to get left and right
    topPoints.sort((a, b) => a.x - b.x);
    const topLeft = topPoints[0];
    const topRight = topPoints[1];

    // Sort bottom points by X to get left and right
    bottomPoints.sort((a, b) => a.x - b.x);
    const bottomLeft = bottomPoints[0];
    const bottomRight = bottomPoints[1];

    // Return in correct order: TL, TR, BR, BL
    return [topLeft, topRight, bottomRight, bottomLeft];
  };

  // Step 3: Define/adjust regions with mouse
  const handleRegionMouseDown = (e, regionIndex) => {
    e.stopPropagation();
    e.preventDefault();
    setActiveRegion(regionIndex);
    setIsDragging(true);

    const rect = e.currentTarget.parentElement.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    if (currentLayout.regions && currentLayout.regions[regionIndex]) {
      setDragStart({
        x: x - currentLayout.regions[regionIndex].x,
        y: y - currentLayout.regions[regionIndex].y,
      });
    }
  };

  const handleRegionMouseMove = (e) => {
    if (!isDragging && !isResizing) return;

    const container = e.currentTarget;
    const rect = container.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;

    if (isDragging && activeRegion !== null && activeRegion !== undefined) {
      setCurrentLayout((prev) => {
        const newRegions = [...prev.regions];
        if (newRegions[activeRegion]) {
          newRegions[activeRegion] = {
            ...newRegions[activeRegion],
            x: Math.max(
              0,
              Math.min(100 - newRegions[activeRegion].width, x - dragStart.x)
            ),
            y: Math.max(
              0,
              Math.min(100 - newRegions[activeRegion].height, y - dragStart.y)
            ),
          };
        }
        return { ...prev, regions: newRegions };
      });
    } else if (
      isResizing &&
      activeRegion !== null &&
      activeRegion !== undefined
    ) {
      const region = currentLayout.regions[activeRegion];
      if (region) {
        setCurrentLayout((prev) => {
          const newRegions = [...prev.regions];
          newRegions[activeRegion] = {
            ...newRegions[activeRegion],
            width: Math.max(10, Math.min(100 - region.x, x - region.x)),
            height: Math.max(5, Math.min(100 - region.y, y - region.y)),
          };
          return { ...prev, regions: newRegions };
        });
      }
    }
  };

  const handleRegionMouseUp = () => {
    setIsDragging(false);
    setIsResizing(false);
  };

  // Handle resize corner
  const handleResizeMouseDown = (e, regionIndex) => {
    e.stopPropagation();
    e.preventDefault();
    setActiveRegion(regionIndex);
    setIsResizing(true);
  };

  // Step 4: Process OCR on defined regions
  const processOCR = async () => {
    setProcessing(true);
    setStep(4);

    try {
      if (!currentLayout || !Array.isArray(currentLayout.regions)) {
        throw new Error("Invalid layout configuration");
      }

      const results = await Promise.all(
        currentLayout.regions.map(async (region) => {
          const text = await processRegion(
            rectifiedImage,
            region,
            region.field
          );
          return { region: region.field, text };
        })
      );

      const extracted = {
        cardNumber: "",
        expirationDate: "",
        cvc: "",
        proxyNumber: "",
      };

      results.forEach((result) => {
        switch (result.region) {
          case "cardNumber":
            extracted.cardNumber = result.text.replace(/\D/g, "").slice(0, 16);
            break;
          case "expirationDate":
            const match = result.text.match(/(\d{2})\D*(\d{2,4})/);
            if (match) {
              const month = match[1];
              let year = match[2];
              if (year.length === 4) year = year.slice(-2);
              extracted.expirationDate = `${month}/${year}`;
            }
            break;
          case "cvc":
            extracted.cvc = result.text.replace(/\D/g, "").slice(0, 4);
            break;
          case "proxyNumber":
            extracted.proxyNumber = result.text.replace(/\D/g, "");
            break;
        }
      });

      setExtractedData(extracted);

      if (extracted.cardNumber) {
        toast.success("Card information extracted!");
      } else {
        toast.warning(
          "Could not extract all information. Please adjust regions and try again."
        );
      }
    } catch (error) {
      console.error("OCR Error:", error);
      toast.error("Failed to process regions");
    } finally {
      setProcessing(false);
    }
  };

  // Process specific region with OCR
  const processRegion = async (imageData, region, regionName) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = async () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        const x = (region.x / 100) * img.width;
        const y = (region.y / 100) * img.height;
        const width = (region.width / 100) * img.width;
        const height = (region.height / 100) * img.height;

        canvas.width = width;
        canvas.height = height;

        ctx.drawImage(img, x, y, width, height, 0, 0, width, height);

        const regionDataUrl = canvas.toDataURL("image/png");

        try {
          // Use field-specific whitelists for better accuracy
          let whitelist = "0123456789";
          if (regionName === "cardNumber") {
            whitelist = "0123456789 ";
          } else if (regionName === "expirationDate") {
            whitelist = "0123456789/";
          } else if (regionName === "cvc") {
            whitelist = "0123456789";
          } else if (regionName === "proxyNumber") {
            whitelist = "0123456789";
          }

          const result = await Tesseract.recognize(regionDataUrl, "eng", {
            tessedit_char_whitelist: whitelist,
          });

          let extractedText = result.data.text.trim();

          // Apply field-specific validation and formatting
          if (regionName === "cardNumber") {
            // Card number: exactly 16 digits
            const digitsOnly = extractedText.replace(/\D/g, "");
            if (digitsOnly.length >= 16) {
              extractedText = digitsOnly.substring(0, 16);
            } else {
              extractedText = digitsOnly;
            }
          } else if (regionName === "expirationDate") {
            // Expiry: MM/YY format
            const digitsOnly = extractedText.replace(/\D/g, "");
            if (digitsOnly.length >= 4) {
              const month = digitsOnly.substring(0, 2);
              const year = digitsOnly.substring(2, 4);
              extractedText = `${month}/${year}`;
            }
          } else if (regionName === "cvc") {
            // CVC: exactly 3 digits
            const digitsOnly = extractedText.replace(/\D/g, "");
            extractedText = digitsOnly.substring(0, 3);
          } else if (regionName === "proxyNumber") {
            // Proxy: exactly 12 digits
            const digitsOnly = extractedText.replace(/\D/g, "");
            if (digitsOnly.length >= 12) {
              extractedText = digitsOnly.substring(0, 12);
            } else {
              extractedText = digitsOnly;
            }
          }

          resolve(extractedText);
        } catch (error) {
          resolve("");
        }
      };
      img.src = imageData;
    });
  };

  // Reset to start
  const reset = () => {
    setStep(1);
    setCapturedImage(null);
    setRectifiedImage(null);
    setCardCorners([]);
    setCardDetected(false); // Reset card detection state
    setRealtimeCorners([]); // Reset realtime corners
    setDetectionConfidence(0); // Reset confidence
    setExtractedData({
      cardNumber: "",
      expirationDate: "",
      cvc: "",
      proxyNumber: "",
    });

    // Restart detection if going back to step 1
    if (window.cv && window.cvReady) {
      const interval = setInterval(() => {
        detectCardInWebcam();
      }, 500);
      setDetectionInterval(interval);
    }
  };

  // Save extracted data
  const handleSave = () => {
    if (!extractedData.cardNumber) {
      toast.error("Card number is required");
      return;
    }

    onCardDataExtracted(extractedData);
    onClose();
  };

  const regionColors = {
    cardNumber: "#3b82f6",
    expirationDate: "#10b981",
    cvc: "#f59e0b",
    proxyNumber: "#8b5cf6",
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Advanced Card OCR Scanner</DialogTitle>
          <DialogDescription>
            Step {step === 1 ? 1 : 2} of 2:{" "}
            {step === 1 ? "Capture Card" : "Extract Information"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Step 1: Capture */}
          {step === 1 && (
            <div className="space-y-4">
              {/* Camera only mode */}
              <div className="space-y-4">
                <div className="relative" style={{ minHeight: "400px" }}>
                  <Webcam
                    ref={webcamRef}
                    audio={false}
                    screenshotFormat="image/jpeg"
                    videoConstraints={{
                      width: 1280,
                      height: 720,
                      facingMode: "environment",
                    }}
                    className="w-full rounded-lg"
                    style={{ minHeight: "400px", objectFit: "cover" }}
                  />

                  {/* Card placement guide with ISO 7810 ratio */}
                  {!cardDetected && (
                    <div className="absolute inset-0 pointer-events-none">
                      {/* Guide rectangle */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div
                          className="relative"
                          style={{
                            aspectRatio: "1.586", // ISO 7810 card ratio
                            height: "60%",
                            maxWidth: "85%",
                          }}
                        >
                          {/* Corner markers */}
                          <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-white/70 rounded-tl-lg"></div>
                          <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-white/70 rounded-tr-lg"></div>
                          <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-white/70 rounded-bl-lg"></div>
                          <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-white/70 rounded-br-lg"></div>

                          {/* Dashed border */}
                          <div className="absolute inset-0 border-2 border-dashed border-white/30 rounded-lg"></div>

                          {/* ISO 7810 label */}
                          <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-black/50 text-white px-3 py-1 rounded text-sm whitespace-nowrap">
                            Place card here
                          </div>

                          {/* Card size indicator */}
                          {/* <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 text-white/50 text-xs">
                              85.6mm × 53.98mm
                            </div> */}

                          {/* OCR Region overlays on guide */}
                          {currentLayout &&
                            Array.isArray(currentLayout.regions) &&
                            currentLayout.regions.map((region, idx) => (
                              <div
                                key={region.field || idx}
                                className="absolute border border-dashed opacity-50 pointer-events-none"
                                style={{
                                  left: `${region.x}%`,
                                  top: `${region.y}%`,
                                  width: `${region.width}%`,
                                  height: `${region.height}%`,
                                  borderColor:
                                    regionColors[region.field] || "#fff",
                                }}
                              >
                                <span
                                  className="absolute -top-5 left-0 text-[10px] px-1 rounded"
                                  style={{
                                    backgroundColor:
                                      regionColors[region.field] || "#3B82F6",
                                    color: "white",
                                  }}
                                >
                                  {region.field === "cardNumber"
                                    ? "Card #"
                                    : region.field === "expirationDate"
                                    ? "Exp"
                                    : region.field === "cvc"
                                    ? "CVC"
                                    : region.field === "proxyNumber"
                                    ? "Proxy"
                                    : region.field}
                                </span>
                              </div>
                            ))}
                        </div>
                      </div>

                      {/* Semi-transparent overlay outside guide area - matching ISO ratio */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div
                          className="absolute"
                          style={{
                            aspectRatio: "1.586", // Same ratio as guide
                            height: "60%",
                            maxWidth: "85%",
                            boxShadow: "0 0 0 9999px rgba(0, 0, 0, 0.3)", // Shadow outside
                            borderRadius: "8px",
                          }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Realtime detection rectangle */}
                  {realtimeCorners.length === 4 && !cardDetected && (
                    <svg className="absolute inset-0 w-full h-full pointer-events-none">
                      <polygon
                        points={realtimeCorners
                          .map((c) => `${c.x}%,${c.y}%`)
                          .join(" ")}
                        fill="none"
                        stroke={
                          detectionConfidence >= 85
                            ? "#10b981"
                            : detectionConfidence >= 60
                            ? "#f59e0b"
                            : "#ef4444"
                        }
                        strokeWidth="3"
                        strokeDasharray={
                          detectionConfidence >= 85 ? "0" : "5,5"
                        }
                      />
                      {realtimeCorners.map((corner, idx) => (
                        <circle
                          key={idx}
                          cx={`${corner.x}%`}
                          cy={`${corner.y}%`}
                          r="6"
                          fill={
                            detectionConfidence >= 85
                              ? "#10b981"
                              : detectionConfidence >= 60
                              ? "#f59e0b"
                              : "#ef4444"
                          }
                        />
                      ))}
                    </svg>
                  )}

                  {/* Card detected overlay */}
                  {cardDetected && (
                    <div className="absolute inset-0 pointer-events-none">
                      <svg className="absolute inset-0 w-full h-full">
                        <polygon
                          points={realtimeCorners
                            .map((c) => `${c.x}%,${c.y}%`)
                            .join(" ")}
                          fill="rgba(16, 185, 129, 0.2)"
                          stroke="#10b981"
                          strokeWidth="4"
                        />
                      </svg>
                      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-green-500 text-white px-4 py-2 rounded-lg">
                        <Check className="h-5 w-5 inline mr-2" />
                        Card Detected! Auto-capturing...
                      </div>
                    </div>
                  )}

                  {/* Detection status with confidence */}
                  <div className="absolute top-2 left-2 bg-black/70 text-white px-3 py-2 rounded-lg">
                    {currentLayout ? (
                      <div className="space-y-1">
                        <div className="text-sm font-medium">
                          {cardDetected ? (
                            <span className="text-green-400">
                              ✓ Card captured
                            </span>
                          ) : realtimeCorners.length === 4 ? (
                            <span
                              className={
                                detectionConfidence >= 85
                                  ? "text-green-400"
                                  : detectionConfidence >= 60
                                  ? "text-yellow-400"
                                  : "text-red-400"
                              }
                            >
                              Card detected
                            </span>
                          ) : (
                            <span className="text-gray-300">
                              Checking OCR regions...
                            </span>
                          )}
                        </div>
                        {realtimeCorners.length === 4 && !cardDetected && (
                          <div className="flex items-center gap-2">
                            <span className="text-xs">Confidence:</span>
                            <div className="flex items-center gap-1">
                              <div className="w-20 h-2 bg-gray-600 rounded-full overflow-hidden">
                                <div
                                  className={`h-full transition-all duration-300 ${
                                    detectionConfidence >= 85
                                      ? "bg-green-500"
                                      : detectionConfidence >= 60
                                      ? "bg-yellow-500"
                                      : "bg-red-500"
                                  }`}
                                  style={{ width: `${detectionConfidence}%` }}
                                />
                              </div>
                              <span className="text-xs font-bold">
                                {detectionConfidence}%
                              </span>
                            </div>
                          </div>
                        )}
                        {realtimeCorners.length === 4 &&
                          detectionConfidence < 85 && (
                            <div className="text-xs text-yellow-300">
                              {detectionConfidence < 60
                                ? "Adjust card position"
                                : "Hold steady..."}
                            </div>
                          )}
                      </div>
                    ) : (
                      <span className="text-yellow-400 text-sm">
                        Loading layout...
                      </span>
                    )}
                  </div>

                  {/* Debug info panel */}
                  <div className="absolute bottom-2 left-2 bg-black/80 text-white px-2 py-1 rounded text-xs space-y-0.5 font-mono">
                    <div className="text-yellow-400 font-bold">DEBUG INFO</div>
                    <div>
                      Layout: {currentLayout ? "✓ Loaded" : "⏳ Loading"}
                    </div>
                    <div>
                      Status:{" "}
                      {debugInfo.detecting ? "🔍 Detecting..." : "⏸ Idle"}
                    </div>
                    <div>
                      OCR Regions: {currentLayout?.regions?.length || 0}
                    </div>
                    <div>Method: Direct OCR</div>
                    <div>Time: {debugInfo.processingTime}ms</div>
                    {debugInfo.lastError && (
                      <div className="text-red-400">
                        Error: {debugInfo.lastError}
                      </div>
                    )}
                    <div className="text-gray-400 text-[10px] mt-1">
                      Layout: {currentLayout?.name || "default"}
                      <br />
                      Regions: {currentLayout?.regions?.length || 0}
                      <br />
                      Auto-capture on OCR match
                    </div>
                  </div>
                </div>
                {/* Auto capture only - no manual button */}
                {!cardDetected && (
                  <div className="text-center text-sm text-muted-foreground">
                    📷 Waiting for card detection...
                  </div>
                )}
                <Alert>
                  <AlertDescription>
                    <strong>Position the card within the camera view</strong>
                    <br />
                    The system will automatically capture when card is detected.
                    Manual capture is disabled - card must be detected first.
                  </AlertDescription>
                </Alert>
              </div>
            </div>
          )}

          {/* Step 2 removed - skip directly to OCR */}
          {false && (
            <div className="space-y-4">
              <Alert>
                <MousePointer className="h-4 w-4" />
                <AlertDescription>
                  {processing ? (
                    <strong>Detecting card corners automatically...</strong>
                  ) : cardCorners.length < 4 ? (
                    <>
                      <strong>
                        Click the 4 corners of the card in any order
                      </strong>
                      <br />
                      <span className="text-sm">
                        Corners selected: {cardCorners.length}/4 •
                        Auto-detection will try to find corners
                      </span>
                    </>
                  ) : (
                    <strong>
                      All corners selected! Drag corners to adjust position or
                      click 'Process Card'
                    </strong>
                  )}
                </AlertDescription>
              </Alert>

              {/* Hidden canvas for processing */}
              <canvas
                ref={rectifiedCanvasRef}
                className="hidden"
                width="1284"
                height="810"
              />

              <div
                className="relative"
                onMouseMove={handleCornerDrag}
                onMouseUp={handleCornerDragEnd}
                onMouseLeave={handleCornerDragEnd}
              >
                {processing ? (
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-10 rounded-lg">
                    <div className="bg-white rounded-lg p-4">
                      <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2" />
                      <p className="text-sm">Rectifying card image...</p>
                    </div>
                  </div>
                ) : null}
                <img
                  src={capturedImage}
                  alt="Captured card"
                  className="w-full rounded-lg cursor-crosshair select-none"
                  onClick={handleImageClick}
                  draggable={false}
                />
                {cardCorners.map((corner, idx) => (
                  <div
                    key={idx}
                    className={`absolute w-4 h-4 bg-red-500 rounded-full -translate-x-1/2 -translate-y-1/2 z-20 ${
                      draggingCorner === idx
                        ? "ring-4 ring-red-300"
                        : "hover:ring-2 hover:ring-red-300"
                    } cursor-move`}
                    style={{ left: `${corner.x}%`, top: `${corner.y}%` }}
                    onMouseDown={(e) => handleCornerMouseDown(e, idx)}
                  >
                    <span className="absolute -top-6 left-1/2 -translate-x-1/2 text-xs bg-red-500 text-white px-1 rounded pointer-events-none">
                      {["TL", "TR", "BR", "BL"][idx]}
                    </span>
                  </div>
                ))}
                {/* Draw lines between corners */}
                {cardCorners.length > 1 && (
                  <svg className="absolute inset-0 w-full h-full pointer-events-none z-10">
                    {cardCorners.map((corner, idx) => {
                      const nextCorner =
                        cardCorners[(idx + 1) % cardCorners.length];
                      if (
                        idx < cardCorners.length - 1 ||
                        cardCorners.length === 4
                      ) {
                        return (
                          <line
                            key={`line-${idx}`}
                            x1={`${corner.x}%`}
                            y1={`${corner.y}%`}
                            x2={`${nextCorner.x}%`}
                            y2={`${nextCorner.y}%`}
                            stroke="red"
                            strokeWidth="2"
                            strokeDasharray="5,5"
                          />
                        );
                      }
                      return null;
                    })}
                  </svg>
                )}
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setCardCorners([]);
                    autoDetectCardCorners();
                  }}
                  disabled={processing}
                >
                  <Maximize2 className="h-4 w-4 mr-1" />
                  Auto Detect
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setCardCorners([])}
                  disabled={processing}
                >
                  <RefreshCw className="h-4 w-4 mr-1" />
                  Reset Corners
                </Button>
                <Button onClick={reset} variant="outline" disabled={processing}>
                  Retake Photo
                </Button>
                {cardCorners.length === 4 && !processing && (
                  <Button onClick={() => rectifyCard(cardCorners)}>
                    <Check className="h-4 w-4 mr-1" />
                    Process Card
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Step 2: Define regions and extract information */}
          {step === 3 && rectifiedImage && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Label>Layout Template:</Label>
                  <Select
                    value={selectedLayout?.name || ""}
                    onValueChange={(name) => {
                      const layout = layouts.find((l) => l.name === name);
                      if (layout) {
                        setSelectedLayout(layout);
                        setCurrentLayout(layout);
                      }
                    }}
                  >
                    <SelectTrigger className="w-[200px]">
                      <SelectValue placeholder="Select layout" />
                    </SelectTrigger>
                    <SelectContent>
                      {layouts.map((layout) => (
                        <SelectItem key={layout.name} value={layout.name}>
                          {layout.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      const newName = `template_${Date.now().toString(36)}`;
                      setCurrentLayout({
                        ...currentLayout,
                        name: newName,
                      });
                      toast.info(
                        "Creating new template - adjust regions and save"
                      );
                    }}
                    title="Create new template"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                  {selectedLayout && selectedLayout.name !== "default" && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => deleteLayout(selectedLayout.name)}
                      className="text-destructive hover:text-destructive"
                      title="Delete this template"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <Input
                    placeholder="Layout name"
                    value={currentLayout.name}
                    onChange={(e) =>
                      setCurrentLayout((prev) => ({
                        ...prev,
                        name: e.target.value,
                      }))
                    }
                    className="w-[150px]"
                  />
                  <Button
                    size="sm"
                    onClick={saveLayout}
                    variant="default"
                    title="Save current regions as template"
                  >
                    <Save className="h-4 w-4 mr-1" />
                    Save Template
                  </Button>
                </div>
              </div>

              {/* Instructions for region editing */}
              <Alert className="border-blue-200 bg-blue-50/50 dark:bg-blue-950/20">
                <Move className="h-4 w-4 text-blue-600" />
                <AlertDescription>
                  <div className="space-y-1">
                    <div className="font-medium text-blue-900 dark:text-blue-100">
                      Adjust OCR regions to match your card layout:
                    </div>
                    <div className="text-sm text-blue-700 dark:text-blue-300">
                      • <strong>Drag regions</strong> to move them over the
                      correct card fields
                      <br />• <strong>Drag corner handles</strong> to resize
                      regions
                      <br />• <strong>Click region buttons</strong> below to
                      select for editing
                    </div>
                  </div>
                </AlertDescription>
              </Alert>

              <div
                className="relative border-2 rounded-lg overflow-hidden"
                onMouseMove={handleRegionMouseMove}
                onMouseUp={handleRegionMouseUp}
                onMouseLeave={handleRegionMouseUp}
              >
                <img
                  src={rectifiedImage}
                  alt="Rectified card"
                  className="w-full h-auto"
                />

                {/* Overlay regions */}
                {currentLayout &&
                  Array.isArray(currentLayout.regions) &&
                  currentLayout.regions.map((region, index) => (
                    <div
                      key={region.field || index}
                      className={`absolute border-2 transition-opacity ${
                        activeRegion === index
                          ? "opacity-100"
                          : "opacity-70 hover:opacity-100"
                      } ${
                        isDragging && activeRegion === index
                          ? "cursor-grabbing"
                          : "cursor-grab"
                      }`}
                      style={{
                        left: `${region.x}%`,
                        top: `${region.y}%`,
                        width: `${region.width}%`,
                        height: `${region.height}%`,
                        borderColor: regionColors[region.field] || "#3B82F6",
                        backgroundColor:
                          activeRegion === index
                            ? `${regionColors[region.field] || "#3B82F6"}30`
                            : `${regionColors[region.field] || "#3B82F6"}20`,
                        boxShadow:
                          activeRegion === index
                            ? `0 0 0 2px ${
                                regionColors[region.field] || "#3B82F6"
                              }40`
                            : "none",
                      }}
                      onMouseDown={(e) => handleRegionMouseDown(e, index)}
                    >
                      <span
                        className="absolute -top-6 left-0 text-xs text-white px-2 py-0.5 rounded font-medium select-none"
                        style={{
                          backgroundColor:
                            regionColors[region.field] || "#3B82F6",
                        }}
                      >
                        {region.field === "cardNumber"
                          ? "Card Number"
                          : region.field === "expirationDate"
                          ? "Expiry"
                          : region.field === "cvc"
                          ? "CVC"
                          : region.field === "proxyNumber"
                          ? "Proxy #"
                          : region.field}
                      </span>

                      {/* Resize handle */}
                      <div
                        className="absolute -bottom-1 -right-1 w-4 h-4 bg-white border-2 rounded-sm cursor-se-resize hover:bg-gray-100"
                        style={{
                          borderColor: regionColors[region.field] || "#3B82F6",
                        }}
                        onMouseDown={(e) => handleResizeMouseDown(e, index)}
                      >
                        <svg
                          className="w-2 h-2 m-0.5 text-gray-500"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            d="M13 7L7 13M13 11L11 13"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                        </svg>
                      </div>

                      {/* Size indicator */}
                      {activeRegion === index && (
                        <span className="absolute bottom-1 left-1 text-xs text-white bg-black bg-opacity-50 px-1 rounded select-none">
                          {Math.round(region.width)}% ×{" "}
                          {Math.round(region.height)}%
                        </span>
                      )}
                    </div>
                  ))}
              </div>

              <div className="grid grid-cols-4 gap-2">
                {currentLayout &&
                  Array.isArray(currentLayout.regions) &&
                  currentLayout.regions.map((region, index) => (
                    <Button
                      key={region.field || index}
                      size="sm"
                      variant={activeRegion === index ? "default" : "outline"}
                      onClick={() => setActiveRegion(index)}
                    >
                      <div
                        className="w-3 h-3 rounded-full mr-2"
                        style={{
                          backgroundColor:
                            regionColors[region.field] || "#3B82F6",
                        }}
                      />
                      {region.field === "cardNumber"
                        ? "Card Number"
                        : region.field === "expirationDate"
                        ? "Expiry"
                        : region.field === "cvc"
                        ? "CVC"
                        : region.field === "proxyNumber"
                        ? "Proxy Number"
                        : region.field}
                    </Button>
                  ))}
              </div>

              <div className="flex gap-2">
                <Button onClick={processOCR} disabled={processing}>
                  <Maximize2 className="h-4 w-4 mr-2" />
                  Process OCR
                </Button>
              </div>
            </div>
          )}

          {/* Step 4: Results */}
          {step === 4 && (
            <div className="space-y-4">
              {processing ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin mr-2" />
                  <span>Processing OCR...</span>
                </div>
              ) : (
                <>
                  <Alert>
                    <Check className="h-4 w-4" />
                    <AlertDescription>
                      Verify and correct the extracted information if needed
                    </AlertDescription>
                  </Alert>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label>Card Number</Label>
                      <Input
                        value={extractedData.cardNumber}
                        onChange={(e) =>
                          setExtractedData({
                            ...extractedData,
                            cardNumber: e.target.value,
                          })
                        }
                        placeholder="1234567890123456"
                        maxLength="16"
                      />
                    </div>

                    <div>
                      <Label>Expiration Date (MM/YY)</Label>
                      <Input
                        value={extractedData.expirationDate}
                        onChange={(e) =>
                          setExtractedData({
                            ...extractedData,
                            expirationDate: e.target.value,
                          })
                        }
                        placeholder="MM/YY"
                        maxLength="5"
                      />
                    </div>

                    <div>
                      <Label>CVC</Label>
                      <Input
                        value={extractedData.cvc}
                        onChange={(e) =>
                          setExtractedData({
                            ...extractedData,
                            cvc: e.target.value,
                          })
                        }
                        placeholder="123"
                        maxLength="4"
                      />
                    </div>

                    <div className="col-span-2">
                      <Label>Proxy Number</Label>
                      <Input
                        value={extractedData.proxyNumber}
                        onChange={(e) =>
                          setExtractedData({
                            ...extractedData,
                            proxyNumber: e.target.value,
                          })
                        }
                        placeholder="Enter proxy number"
                      />
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button onClick={() => setStep(3)} variant="outline">
                      Adjust Regions
                    </Button>
                    <Button onClick={processOCR} variant="outline">
                      <RefreshCw className="h-4 w-4 mr-1" />
                      Retry OCR
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex justify-between">
          <div className="flex-1">
            {step === 1 && (
              <Button
                variant="ghost"
                onClick={() => {
                  // Capture current frame and go to layout editing
                  if (webcamRef.current) {
                    const capturedImg = webcamRef.current.getScreenshot();
                    if (capturedImg) {
                      setCapturedImage(capturedImg);
                      setRectifiedImage(capturedImg);
                      setStep(3);
                      toast.success("Image captured! Now adjust OCR regions.");
                    } else {
                      toast.error("Failed to capture image");
                    }
                  } else {
                    toast.error("Camera not ready");
                  }
                }}
                className="mr-auto"
              >
                <Settings className="h-4 w-4 mr-2" />
                Capture & Edit Layout
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            {step === 3 && (
              <Button
                variant="outline"
                onClick={() => {
                  // Go back to capture
                  setStep(1);
                  setCardDetected(false);
                }}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Recapture
              </Button>
            )}
            {step === 3 && Object.keys(extractedData).some((key) => extractedData[key]) && (
              <Button onClick={handleSave}>
                <Check className="h-4 w-4 mr-2" />
                Save Card Information
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
